import { usePageTitle } from '@/hooks/usePageTitle';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Calculator, Check, RotateCcw, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCurrency } from '@/lib/format';
import productApi from '@/lib/productApi';
import { toast } from '@/lib/toast';
import PublishDialog from '@/components/products/PublishDialog';
import ProductHistory from '@/components/products/ProductHistory';
import InfoHint from '@/components/products/InfoHint';
import CategoryPicker from '@/components/products/CategoryPicker';
import LabelInput from '@/components/products/LabelInput';
import ProductVariantsFields from '@/components/products/ProductVariantsFields';
import ProductReviewersDialog from '@/components/products/ProductReviewersDialog';
import ReviewerGroupsDialog from '@/components/products/ReviewerGroupsDialog';
import {
  BILLING_PERIOD_LABELS,
  type ProductComputation,
  type ProductDetail,
  type ProductSpec,
  type SpecValidationIssue,
} from '@/types/product';
import PageContainer from '@/components/layout/PageContainer';

const money = (cents: number | null | undefined) =>
  cents === null || cents === undefined ? '—' : formatCurrency(cents / 100);

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

function ProductEditorPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<ProductDetail | null>(null);
  const [spec, setSpec] = useState<ProductSpec | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [meta, setMeta] = useState({
    name: '',
    category: '',
    labels: [] as string[],
  });
  const [computation, setComputation] = useState<ProductComputation | null>(null);
  const [issues, setIssues] = useState<SpecValidationIssue[]>([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [reviewersOpen, setReviewersOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [taxonomy, setTaxonomy] = useState<{ categories: string[]; labels: string[] }>({
    categories: [],
    labels: [],
  });

  usePageTitle('Product bewerken', detail?.id === id ? meta.name : undefined);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productApi.get(id);
      setDetail(data);
      setSpec(data.draftSpec);
      setSelectedVariantId(data.draftSpec.variants[0]?.id ?? '');
      setComputation(data.computation);
      setIssues(data.issues);
      setMeta({
        name: data.name,
        category: data.category ?? '',
        labels: data.labels,
      });
      setDirty(false);
    } catch (error) {
      toast.error('Product kon niet geladen worden', { error });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Bestaande categorieën en labels, zodat je kunt kiezen in plaats van typen.
  useEffect(() => {
    productApi
      .taxonomy()
      .then(setTaxonomy)
      .catch(() => undefined);
  }, []);

  // Live preview: de engine draait op de server, zodat er één implementatie is
  // die publiceren en previewen deelt.
  const previewTimer = useRef<number>();
  useEffect(() => {
    if (!spec || !dirty) return;
    window.clearTimeout(previewTimer.current);
    previewTimer.current = window.setTimeout(() => {
      productApi
        .preview(spec)
        .then((result) => {
          setComputation(result.computation);
          setIssues(result.issues);
        })
        .catch(() => undefined);
    }, 400);
    return () => window.clearTimeout(previewTimer.current);
  }, [spec, dirty]);

  const patchSpec = (next: ProductSpec) => {
    setSpec(next);
    setDirty(true);
  };

  const activeVariant =
    spec?.variants.find((variant) => variant.id === selectedVariantId) ?? spec?.variants[0];
  const variantById = useMemo(
    () => new Map((spec?.variants ?? []).map((variant) => [variant.id, variant])),
    [spec]
  );

  const visibleComputations = (computation?.variants ?? []).filter((variant) =>
    variantById.has(variant.variantId)
  );

  /** Verplichte reviewers eerst, daarna elke gekoppelde keuzegroep. */
  const reviewerSections = useMemo(() => {
    const required = detail?.reviewers ?? [];
    const groups = detail?.reviewGroups ?? [];
    return [
      ...(required.length
        ? [{ key: '__required__', title: 'Altijd akkoord nodig', reviewers: required }]
        : []),
      ...groups.map((group) => ({
        key: group.id,
        title: `${group.name} — 1 van ${group.memberCount} beoordeelt`,
        reviewers: group.members ?? [],
      })),
    ];
  }, [detail?.reviewGroups, detail?.reviewers]);

  /** Velden die bij elke opslag meegaan; ook bij 'markeer als nagekeken'. */
  const metaPayload = () => ({
    name: meta.name,
    category: meta.category || undefined,
    labels: meta.labels,
  });

  const handleSave = async () => {
    if (!spec) return false;
    setSaving(true);
    try {
      const result = await productApi.saveDraft(id, { ...metaPayload(), spec });
      setIssues(result.issues);
      setComputation(result.computation);
      setDirty(false);
      toast.success('Concept opgeslagen');
      return true;
    } catch (error) {
      toast.error('Opslaan is niet gelukt', { error });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = async () => {
    try {
      await productApi.discardDraft(id);
      await load();
      toast.info('Concept verworpen');
    } catch (error) {
      toast.error('Verwerpen is niet gelukt', { error });
    }
  };

  if (loading || !spec || !detail) {
    return <div className="p-8 text-muted-foreground">Laden…</div>;
  }

  return (
    <PageContainer className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
        <div className="flex items-start gap-3">
          <div>
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="-ml-1 mb-1 inline-flex items-center gap-1.5 rounded px-1 py-0.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none"
            >
              <ArrowLeft className="h-4 w-4" />
              Alle producten
            </button>
            <h1 className="text-2xl font-semibold">{meta.name || 'Naamloos product'}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {detail.currentVersion ? (
                <Badge variant="outline">Versie {detail.currentVersion.version} gepubliceerd</Badge>
              ) : (
                <Badge variant="secondary">Nog niet gepubliceerd</Badge>
              )}
              {dirty && <Badge variant="secondary">Niet-opgeslagen wijzigingen</Badge>}
              {detail.usageCount > 0 && <span>{detail.usageCount} offerteregels gekoppeld</span>}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {detail.currentVersion && (
            <Button variant="outline" onClick={handleDiscard}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Concept verwerpen
            </Button>
          )}
          <Button variant="outline" onClick={handleSave} disabled={saving}>
            {saving ? 'Opslaan…' : 'Concept opslaan'}
          </Button>
          <Button onClick={() => setPublishOpen(true)} disabled={dirty || issues.length > 0}>
            <Check className="mr-2 h-4 w-4" />
            Publiceren
          </Button>
        </div>
      </div>

      {dirty && (
        <Alert>
          <AlertDescription>
            Sla het concept eerst op; publiceren werkt met wat er is opgeslagen.
          </AlertDescription>
        </Alert>
      )}

      {issues.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Dit concept kan nog niet gepubliceerd worden:</strong>
            <ul className="mt-1 list-inside list-disc text-sm">
              {issues.map((issue) => (
                <li key={`${issue.field}-${issue.message}`}>{issue.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {detail.componentUpdates.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Onderdeel gewijzigd.</strong> Deze bundel staat op een oudere versie van:{' '}
            {detail.componentUpdates
              .map(
                (update) =>
                  `${update.productName} (v${update.pinnedVersion} → v${update.latestVersion})`
              )
              .join(', ')}
            . De bundel wijzigt pas als je de nieuwe versie hier overneemt en opnieuw publiceert.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Algemeen</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="name">Naam</Label>
                <Input
                  id="name"
                  value={meta.name}
                  onChange={(event) => {
                    setMeta({ ...meta, name: event.target.value });
                    setDirty(true);
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="category" className="flex items-center gap-1.5">
                  Categorie
                  <InfoHint label="Categorie">
                    De hoofdindeling van de catalogus, bijvoorbeeld Website &amp; development of
                    Marketing. Elk product hoort bij precies één categorie; sales filtert hierop bij
                    het toevoegen aan een offerte. Kies er een uit de lijst — een nieuwe typen kan,
                    maar levert een aparte groep op.
                  </InfoHint>
                </Label>
                <CategoryPicker
                  id="category"
                  value={meta.category}
                  existing={taxonomy.categories}
                  onChange={(next) => {
                    setMeta({ ...meta, category: next });
                    setDirty(true);
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="labels" className="flex items-center gap-1.5">
                  Labels
                  <InfoHint label="Labels">
                    Vrije kenmerken naast de categorie, bijvoorbeeld opstart, recurring, eenmalig of
                    optioneel. Een product kan er meerdere hebben, en je kunt er in de
                    productenlijst op filteren. Anders dan de categorie zijn ze niet verplicht.
                  </InfoHint>
                </Label>
                <LabelInput
                  id="labels"
                  value={meta.labels}
                  existing={taxonomy.labels}
                  onChange={(next) => {
                    setMeta({ ...meta, labels: next });
                    setDirty(true);
                  }}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden">
            <ProductVariantsFields
              spec={spec}
              activeVariantId={activeVariant?.id ?? ''}
              onSelect={setSelectedVariantId}
              onChange={patchSpec}
            />
            <div className="flex flex-wrap items-center justify-between gap-3 border-t px-6 py-4">
              <p className="text-sm text-muted-foreground">
                Je bewerkt uren, tarieven en verkoopprijzen in het productwerkblad.
              </p>
              <Button
                variant="outline"
                size="sm"
                disabled={saving}
                onClick={async () => {
                  if (dirty) {
                    const saved = await handleSave();
                    if (saved === false) return;
                  }
                  navigate(`/products?expand=${encodeURIComponent(id ?? '')}`);
                }}
              >
                Prijsopbouw bekijken
              </Button>
            </div>

            <section aria-labelledby="variant-cost-title" className="border-t">
              <CardHeader>
                <h3
                  id="variant-cost-title"
                  className="flex items-center gap-1.5 text-sm font-semibold"
                >
                  Kostprijs en marge voor {activeVariant?.name || 'Naamloze variant'}
                  <InfoHint label="Kostprijs en marge">
                    Wat dit product jou kost, bijvoorbeeld een licentie of ingehuurde uren. Alleen
                    zichtbaar in de catalogus en boven een offerte — de klant ziet er niets van.
                    Kies <strong>Kostprijs met opslag</strong> als de verkoopprijs uit de kostprijs
                    volgt, zoals de 20% op tooling.
                  </InfoHint>
                </h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <Select
                  value={spec.cost.mode}
                  onValueChange={(value) =>
                    patchSpec({
                      ...spec,
                      cost: { ...spec.cost, mode: value as ProductSpec['cost']['mode'] },
                    })
                  }
                >
                  <SelectTrigger className="w-full sm:w-72">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Niet bijhouden</SelectItem>
                    <SelectItem value="amount">Kostprijs invullen</SelectItem>
                    <SelectItem value="markup">
                      Kostprijs met opslag (verkoopprijs volgt)
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  De kostprijsmethode en opslag gelden voor alle varianten. Het bedrag stel je per
                  variant in.
                </p>

                {spec.cost.mode === 'markup' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="markup">Opslag in procenten</Label>
                    <Input
                      id="markup"
                      type="number"
                      className="w-32"
                      value={spec.cost.markupPct ?? ''}
                      onChange={(event) =>
                        patchSpec({
                          ...spec,
                          cost: { ...spec.cost, markupPct: Number(event.target.value || 0) },
                        })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Voor tooling staat in de bronsheet 20%.
                    </p>
                  </div>
                )}

                {spec.cost.mode !== 'none' && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {(activeVariant ? [activeVariant] : []).map((variant) => (
                      <div key={variant.id} className="space-y-1.5">
                        <Label htmlFor={`cost-${variant.id}`}>Kostprijs {variant.name}</Label>
                        <Input
                          id={`cost-${variant.id}`}
                          type="number"
                          step="0.01"
                          value={(
                            (spec.cost.amountCentsByVariant?.[variant.id] ?? 0) / 100
                          ).toString()}
                          onChange={(event) =>
                            patchSpec({
                              ...spec,
                              cost: {
                                ...spec.cost,
                                amountCentsByVariant: {
                                  ...spec.cost.amountCentsByVariant,
                                  [variant.id]: Math.round(Number(event.target.value || 0) * 100),
                                },
                              },
                            })
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </section>
          </Card>

          <ProductHistory productId={id} />
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4" />
                  Reviewers
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => setReviewersOpen(true)}>
                  Beheren
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {reviewerSections.length > 0 ? (
                <div className="space-y-4">
                  {reviewerSections.map((section) => (
                    <div key={section.key} className="space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {section.title}
                      </p>
                      {section.reviewers.map((reviewer) => (
                        <div key={reviewer.ownerId} className="flex min-w-0 items-center gap-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#E0F3FA] text-[11px] font-semibold text-[#006B7C]">
                            {initials(reviewer.name)}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">
                              {reviewer.name}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {reviewer.email}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nog geen reviewers ingesteld.</p>
              )}
              <p className="text-xs leading-5 text-muted-foreground">
                Offertes met dit product moeten door elke verplichte reviewer worden goedgekeurd.
                Uit een keuzegroep wijst de opsteller per offerte één verantwoordelijke aan.
              </p>
            </CardContent>
          </Card>

          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Calculator className="h-4 w-4" />
                Productprijs
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {visibleComputations.map((variant) => (
                <div key={variant.variantId} className="rounded-md border p-3">
                  <div className="flex items-baseline justify-between">
                    <span className="font-medium">
                      {variantById.get(variant.variantId)?.name ?? variant.variantId}
                    </span>
                    <span className="text-lg font-semibold">
                      {spec.priceMode === 'on_request'
                        ? spec.onRequestText || 'Op aanvraag'
                        : money(variant.priceCents)}
                    </span>
                  </div>
                  <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                    {spec.priceMode === 'hours' && <div>{variant.totalHours} uur</div>}
                    <div>{BILLING_PERIOD_LABELS[detail.billingPeriod]}</div>
                    {variant.marginCents !== null && (
                      <div>
                        Marge {money(variant.marginCents)}
                        {variant.marginPct !== null ? ` (${variant.marginPct}%)` : ''}
                      </div>
                    )}
                  </div>
                  {variant.errors.length > 0 && (
                    <ul className="mt-2 list-inside list-disc text-xs text-destructive">
                      {variant.errors.map((error) => (
                        <li key={error.message}>{error.message}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      <PublishDialog
        productId={id}
        productName={meta.name}
        open={publishOpen}
        onOpenChange={setPublishOpen}
        onPublished={() => {
          void load();
          toast.success('Product gepubliceerd');
        }}
      />

      <ProductReviewersDialog
        open={reviewersOpen}
        products={[detail]}
        onOpenChange={setReviewersOpen}
        onSaved={() => {
          // Groepsleden en aantallen komen van de server; opnieuw laden houdt het
          // paneel gelijk aan wat er werkelijk is opgeslagen.
          void load();
          toast.success('Reviewers opgeslagen');
        }}
        onManageGroups={() => {
          setReviewersOpen(false);
          setGroupsOpen(true);
        }}
        onError={(message, error) => toast.error(message, { error })}
      />

      <ReviewerGroupsDialog
        open={groupsOpen}
        onOpenChange={setGroupsOpen}
        onChanged={() => void load()}
      />
    </PageContainer>
  );
}

export default ProductEditorPage;
